import { useQuery } from '@tanstack/react-query';
import type { Solicitacao, Contato } from '@eleg360/types';
import type { Acao } from '@/lib/api/acoes';
import { contatosApi } from '@/lib/api/contatos';
import { useAuthStore } from '@/lib/stores/authStore';
import { podeEnviarWhatsApp } from '@/lib/lgpd';
import { COLUNAS } from './SolicitacoesPage';

/**
 * Aba "Painéis" de Solicitações (proto:1201-1208).
 *
 * ── Os cards são navegação, não leitura ────────────────────────────────────
 * No protótipo cada barra e cada linha da tabela reescreve `STATE.sol` e
 * re-renderiza a tela inteira. Aqui isso vira `onFiltrar`, que o
 * `SolicitacoesPage` aplica aos MESMOS dados que alimentam a lista, o kanban e
 * estes painéis — recortar por uma barra recorta tudo, como no protótipo.
 *
 * ── HONESTIDADE DE DADO: o que o protótipo crava e esta tela não reproduz ──
 * Três blocos do protótipo exibem número de demonstração:
 *
 * 1. `Evolução · 8 semanas` — a série é `[14,18,22,19,26,31,28,abertas.length]`;
 *    sete das oito semanas são literais. AQUI É DADO REAL: a série é contada a
 *    partir de `Solicitacao.created_at`, que a API de solicitações expõe
 *    (`GET /gab/solicitacoes`, `packages/types` → `Solicitacao.created_at`).
 *    O eixo também deixou de ser o literal "jul": é o mês em que a janela de 8
 *    semanas realmente começa.
 *
 * 2. `Evolução` · rodapé — "Entrada média 19/semana · saída 16/semana · backlog
 *    crescendo". A ENTRADA é real (mesma contagem por `created_at`). A SAÍDA e a
 *    tendência de backlog são LACUNA DECLARADA: a data em que uma solicitação
 *    foi concluída só existe como evento (`gab.solicitacao_evento`, tipo
 *    `status_alterado`, gravado em `solicitacoes.service.ts`) e a API só expõe
 *    esses eventos UMA SOLICITAÇÃO POR VEZ
 *    (`GET /gab/solicitacoes/:id/eventos`) — não há endpoint agregado.
 *    `updated_at` não serve de substituto: qualquer edição o move.
 *
 * 3. `Funil por status` · "Tempo médio" — "recebida → análise 2,1 d · …".
 *    LACUNA DECLARADA pela mesma causa raiz do item 2: as datas de transição
 *    existem no evento, não na solicitação, e não há rota que as agregue.
 *
 * Nada aqui é estimado ou interpolado. Onde o número não existe, o card diz
 * qual é a causa — mesmo padrão de `ControleVotacaoPage.tsx` e
 * `HistoricoSecao.tsx`.
 *
 * ── A regra vale também para AFIRMAÇÃO, não só para número ─────────────────
 * "Tema recorrente sem ação" compara o tema da solicitação com o tema da ação.
 * Enquanto Ações usou uma constante fixa de 7 itens no front e Solicitações leu
 * as categorias do mandato, os dois conjuntos não tinham UM literal em comum: a
 * nota acusava sempre o tema mais demandado de não ter ação, mesmo quando tinha,
 * e oferecia o link "Criar ação" — levando a duplicar ação já existente. Hoje as
 * duas telas leem o mesmo catálogo (`lib/hooks/useTemas.ts`), como no protótipo,
 * onde `SOLIC` e `ACOES` sempre saíram de um `TEMAS` só.
 */

export type FiltroPainel = {
  bairro?: string;
  tema?: string;
  status?: string;
  responsavel?: string;
};

const SEMANAS = 8;
const DIA = 86_400_000;

/** `saudeChip` do protótipo (proto:833): 65+ saudável, 45+ atenção, abaixo crítico. */
function ChipSaude({ v }: { v: number }) {
  const t = v >= 65 ? 'good' : v >= 45 ? 'warn' : 'crit';
  return <span className={`chip ${t}`}>{v}</span>;
}

type Barra = { chave: string; rotulo: string; valor: number };

function Hbar({
  dados,
  cor,
  aoClicar,
}: {
  dados: Barra[];
  cor?: string;
  aoClicar: (chave: string) => void;
}) {
  const max = Math.max(1, ...dados.map((d) => d.valor));
  if (dados.length === 0) return <div className="empty">Sem dados no recorte.</div>;
  return (
    <div className="rank">
      {dados.map((d) => (
        <button key={d.chave} type="button" onClick={() => aoClicar(d.chave)}>
          <span style={{ width: 110, textAlign: 'left' }}>{d.rotulo}</span>
          <span className="b">
            <i style={{ width: `${(d.valor / max) * 100}%`, background: cor || 'var(--accent)' }} />
          </span>
          <b className="num" style={{ width: 34, textAlign: 'right' }}>
            {d.valor}
          </b>
        </button>
      ))}
    </div>
  );
}

/** Ordena decrescente e descarta as fatias vazias, como `.filter(x=>x.v)` do protótipo. */
function agrupar(itens: string[]): Barra[] {
  const mapa = new Map<string, number>();
  itens.forEach((i) => mapa.set(i, (mapa.get(i) || 0) + 1));
  return [...mapa.entries()]
    .map(([chave, valor]) => ({ chave, rotulo: chave, valor }))
    .filter((b) => b.valor > 0)
    .sort((a, b) => b.valor - a.valor);
}

const primeiroNome = (n: string) => n.split(' ')[0] || n;
const doisPrimeiros = (n: string) => n.split(' ').slice(0, 2).join(' ');

export interface PainelSolicitacoesProps {
  /** Já recortado pelos filtros da tela — é o `L` do protótipo. */
  solicitacoes: Solicitacao[];
  contatos: Contato[];
  usuarios: Array<{ id: string; nome: string }>;
  acoes: Acao[];
  onFiltrar: (f: FiltroPainel) => void;
  onAbrirContato: (contatoId: string) => void;
  onCriarAcao: (tema?: string) => void;
}

export function PainelSolicitacoes({
  solicitacoes,
  contatos,
  usuarios,
  acoes,
  onFiltrar,
  onAbrirContato,
  onCriarAcao,
}: PainelSolicitacoesProps) {
  const token = useAuthStore((s) => s.token) || '';
  const agora = Date.now();
  const contatoDe = (id?: string) => contatos.find((c) => c.id === id);
  const estaVencida = (s: Solicitacao) =>
    s.status !== 'concluida' && !!s.sla_prazo && new Date(s.sla_prazo).getTime() < agora;

  const total = solicitacoes.length;

  const porBairro = agrupar(solicitacoes.map((s) => s.bairro).filter(Boolean) as string[]);
  const porTema = agrupar(solicitacoes.map((s) => s.tema).filter(Boolean) as string[]);

  // Solicitantes mais frequentes — top 5, como no protótipo.
  const contagemPorContato = new Map<string, number>();
  solicitacoes.forEach((s) => {
    if (s.contato_id) contagemPorContato.set(s.contato_id, (contagemPorContato.get(s.contato_id) || 0) + 1);
  });
  const topBruto = [...contagemPorContato.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5)
    .map(([id, v]) => ({ id, valor: v, contato: contatoDe(id) }));

  /**
   * Os cinco solicitantes do topo, buscados pelo id quando não estão na página
   * de contatos carregada pela tela (`contatosApi.list({ limit: 500 })`).
   *
   * Sem isso, num mandato com mais de 500 contatos, quem estivesse além da
   * página seria contado como SEM OPT-IN e FORA DA REDE DE LIDERANÇA — dois
   * números errados em silêncio no card de LGPD — e ainda apareceria com o ULID
   * cru no rótulo da barra. É a mesma busca por id que `ContatosPage` já faz
   * quando recebe `?contato=`.
   */
  const idsFaltando = topBruto.filter((t) => !t.contato).map((t) => t.id);
  const { data: buscados = [] } = useQuery({
    queryKey: ['contatos', 'frequentes', idsFaltando],
    queryFn: async () => {
      const r = await Promise.allSettled(idsFaltando.map((id) => contatosApi.get(id, token)));
      return r.flatMap((x) => (x.status === 'fulfilled' ? [x.value as Contato] : []));
    },
    enabled: !!token && idsFaltando.length > 0,
  });

  const topSol = topBruto.map((t) => ({
    ...t,
    contato: t.contato ?? buscados.find((c) => c.id === t.id),
  }));

  // Os agregados de LGPD só contam cadastro que a tela realmente tem em mãos;
  // "cadastro não carregado" é declarado à parte, nunca somado a "sem opt-in".
  const comCadastro = topSol.filter((t) => !!t.contato);
  const semCadastro = topSol.length - comCadastro.length;
  // "rede de liderança": no protótipo o contato está na lista de uma liderança;
  // no schema real isso é `Contato.lideranca_id`.
  const naRede = comCadastro.filter((t) => !!t.contato?.lideranca_id).length;
  const semOptin = comCadastro.filter((t) => !podeEnviarWhatsApp(t.contato!)).length;

  // Carga por responsável. Solicitações sem responsável ganham a própria linha
  // em vez de sumirem da conta.
  const idsResp = [...new Set(solicitacoes.map((s) => s.responsavel_id || ''))];
  const carga = idsResp
    .map((id) => {
      const m = solicitacoes.filter((s) => (s.responsavel_id || '') === id);
      const venc = m.filter(estaVencida).length;
      return {
        id,
        nome: id ? usuarios.find((u) => u.id === id)?.nome || 'Usuário removido' : 'Sem responsável',
        tot: m.length,
        ab: m.filter((s) => s.status !== 'concluida').length,
        venc,
        conc: m.filter((s) => s.status === 'concluida').length,
        sla: m.length ? Math.round(((m.length - venc) / m.length) * 100) : 100,
      };
    })
    .sort((a, b) => b.tot - a.tot);

  // Mesma lista de status do Kanban e da Lista — um rótulo só por status.
  const funil = COLUNAS.map((st) => ({
    chave: st.v,
    rotulo: st.l,
    valor: solicitacoes.filter((s) => s.status === st.v).length,
  }));

  // ── Evolução: 8 semanas contadas por `created_at` (dado real) ──────────────
  const semanas = Array.from({ length: SEMANAS }, () => 0);
  // Índice da semana mais antiga com abertura no recorte: a média divide por
  // este trecho, não pelas 8 semanas inteiras. Num mandato (ou num recorte) com
  // três semanas de histórico, dividir por 8 subestimaria a entrada em 60%.
  let primeiraComDado = SEMANAS;
  solicitacoes.forEach((s) => {
    const t = new Date(s.created_at).getTime();
    if (Number.isNaN(t)) return;
    const dias = Math.floor((agora - t) / DIA);
    if (dias < 0 || dias >= SEMANAS * 7) return;
    const i = SEMANAS - 1 - Math.floor(dias / 7);
    semanas[i]! += 1;
    if (i < primeiraComDado) primeiraComDado = i;
  });
  const maxSemana = Math.max(1, ...semanas);
  const abertasNaJanela = semanas.reduce((a, b) => a + b, 0);
  const semanasComHistorico = SEMANAS - primeiraComDado;
  const entradaMedia = (abertasNaJanela / Math.max(1, semanasComHistorico)).toLocaleString(
    'pt-BR',
    { maximumFractionDigits: 1 },
  );
  const inicioJanela = new Date(agora - (SEMANAS * 7 - 1) * DIA)
    .toLocaleDateString('pt-BR', { month: 'short' })
    .replace('.', '');

  // ── Leitura estratégica ───────────────────────────────────────────────────
  const lider = porBairro[0];
  const pctLider = lider && total ? Math.round((lider.valor / total) * 100) : 0;
  const vencidas = solicitacoes.filter(estaVencida).length;
  const menosAbertas = [...carga].sort((a, b) => a.ab - b.ab)[0];

  const emCurso = (a: Acao) => a.status !== 'concluida' && a.status !== 'cancelada';
  const temaSemAcao = porTema.find(
    (t) => !acoes.some((a) => a.tema === t.chave && emCurso(a)),
  );

  if (total === 0) {
    return (
      <div className="empty" style={{ marginTop: 12 }}>
        Nenhuma solicitação no recorte atual: os painéis não têm o que agregar.
      </div>
    );
  }

  return (
    <div className="grid g3" style={{ marginTop: 12, alignItems: 'start' }}>
      <div className="card">
        <h2>Ranking por território</h2>
        <Hbar dados={porBairro} aoClicar={(b) => onFiltrar({ bairro: b })} />
        <p style={{ fontSize: 12, color: 'var(--ink3)', margin: '6px 0 0' }}>clique para filtrar</p>
      </div>

      <div className="card">
        <h2>Ranking por tema</h2>
        <Hbar dados={porTema} cor="var(--warn)" aoClicar={(t) => onFiltrar({ tema: t })} />
      </div>

      <div className="card">
        <h2>Solicitantes mais frequentes</h2>
        <Hbar
          dados={topSol.map((t) => ({
            chave: t.id,
            // Nunca o ULID cru: quando o cadastro não veio, o rótulo diz isso.
            rotulo: t.contato ? doisPrimeiros(t.contato.nome) : 'Cadastro não carregado',
            valor: t.valor,
          }))}
          cor="var(--good)"
          aoClicar={onAbrirContato}
        />
        <p style={{ fontSize: 12, color: 'var(--ink3)', margin: '6px 0 0' }}>
          {naRede} deles são rede de liderança
          {semCadastro > 0
            ? ` (de ${comCadastro.length} com cadastro carregado; ${semCadastro} não voltaram da API de contatos e ficaram fora desta conta)`
            : ''}
        </p>
      </div>

      <div className="card">
        <h2>Carga por responsável</h2>
        <div className="tw">
          <table>
            <thead>
              <tr>
                <th>Responsável</th>
                <th>Abertas</th>
                <th>Vencidas</th>
                <th>Concluídas</th>
                <th>SLA</th>
              </tr>
            </thead>
            <tbody>
              {carga.map((r) => (
                <tr
                  key={r.id || 'sem'}
                  className="row"
                  style={{ cursor: 'pointer' }}
                  onClick={() => onFiltrar({ responsavel: r.id || 'sem' })}
                >
                  <td>{r.nome}</td>
                  <td className="num">{r.ab}</td>
                  <td className="num" style={{ color: r.venc ? 'var(--crit)' : undefined }}>
                    {r.venc}
                  </td>
                  <td className="num">{r.conc}</td>
                  <td>
                    <ChipSaude v={r.sla} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card">
        <h2>Funil por status</h2>
        <Hbar dados={funil} cor="var(--accent)" aoClicar={(s) => onFiltrar({ status: s })} />
        <dl className="dl" style={{ marginTop: 8 }}>
          <dt>Tempo médio</dt>
          <dd>
            Indisponível: a data de cada mudança de status fica no evento
            <code> gab.solicitacao_evento </code>e a API só a devolve uma solicitação por vez
            (<code>GET /gab/solicitacoes/:id/eventos</code>), sem rota agregada. Enquanto não
            houver, este card não estima o tempo de transição.
          </dd>
        </dl>
      </div>

      <div className="card">
        <h2>Evolução · 8 semanas</h2>
        <div className="hist">
          {semanas.map((n, i) => (
            <i
              key={i}
              title={`Semana ${i + 1}: ${n} abertas`}
              style={{ height: `${Math.round((n / maxSemana) * 100)}%` }}
            />
          ))}
        </div>
        <p className="lbl">
          <span>{inicioJanela}</span>
          <span>hoje</span>
        </p>
        <p style={{ fontSize: 12, color: 'var(--ink3)', margin: '6px 0 0' }}>
          {abertasNaJanela === 0
            ? 'Nenhuma solicitação aberta nas 8 semanas do recorte: não há entrada média a calcular. '
            : `Entrada média ${entradaMedia}/semana nas ${semanasComHistorico} semana(s) desde a abertura mais antiga do recorte, contada pela data de abertura real. `}
          Saída por semana
          e tendência do backlog ficam de fora: a data de conclusão só existe em
          <code> gab.solicitacao_evento </code>(exposto uma solicitação por vez) e
          <code> updated_at </code>muda a cada edição, então não serve de data de conclusão.
        </p>
      </div>

      <div className="card" style={{ gridColumn: 'span 3' }}>
        <h2>Leitura estratégica</h2>
        <div className="grid g3">
          <div className="note crit">
            <b>Pressão × capacidade</b>
            {lider && menosAbertas
              ? `${lider.rotulo} concentra ${pctLider}% das solicitações e ${vencidas} vencidas no recorte. Sugestão: mutirão de atendimento no território + redistribuir para ${primeiroNome(menosAbertas.nome)}.`
              : 'Sem território informado nas solicitações do recorte: não há concentração a medir.'}
          </div>
          <div className="note warn">
            <b>Tema recorrente sem ação</b>
            {temaSemAcao
              ? `${temaSemAcao.rotulo} tem ${temaSemAcao.valor} solicitações e nenhuma ação do mandato em curso; candidato a indicação/emenda.`
              : 'Todos os temas com demanda têm ação em curso.'}{' '}
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                onCriarAcao(temaSemAcao?.chave);
              }}
            >
              Criar ação
            </a>
          </div>
          <div className="note good">
            <b>Oportunidade de relacionamento</b>
            {`${topSol.length} solicitantes frequentes; ${semOptin} sem opt-in; registrar consentimento no próximo atendimento amplia o retorno automático.`}
            {semCadastro > 0
              ? ` ${semCadastro} deles não tiveram o cadastro devolvido pela API de contatos: não estão nesta conta, nem como com nem como sem opt-in.`
              : ''}
          </div>
        </div>
      </div>
    </div>
  );
}
